import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  CreditCardIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { invoicesService } from '../../../services/accounting/invoices';
import { clientsService } from '../../../services/logistics/clients';
import { shipmentsService } from '../../../services/logistics/shipments';
import { companySettingsService } from '../../../services/system/company-settings';
import { journalEntriesService } from '../../../services/accounting/journal-entries';
import { formatCurrency, formatDate, ensureEnglishNumerals } from '../../../utils/formatting';
import toast from 'react-hot-toast';
import InvoiceDetailModal from '../../../pages/InvoiceDetailModal';
import EditInvoiceModal from '../modals/EditInvoiceModal';
import RecordPaymentModal from '../RecordPaymentModal';


import CreateInvoiceModal from '../modals/CreateInvoiceModal';
import { invoicePDFService } from '../../../services/pdf/invoice-pdf';

/**
 * InvoicesTab Component
 * 
 * Comprehensive invoice management interface that handles:
 * - Invoice listing with search and filtering
 * - Invoice creation, editing, and deletion
 * - Status management and payment recording
 * - PDF generation and download
 * - Summary statistics display
 * 
 * Features:
 * - Real-time invoice data loading
 * - Client information integration
 * - Status-based filtering and actions
 * - Direct PDF download functionality
 * - Payment recording integration
 * 
 * Translation Support:
 * - All user-facing text uses i18n translation keys
 * - Supports both Arabic and English interfaces
 * - Maintains RTL compatibility
 */
const InvoicesTab = () => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeInvoice, setStatusChangeInvoice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);

  /**
   * Load invoices data with client information
   * Handles error cases and provides fallback data structure
   */
  const loadInvoices = useCallback(async () => {
    if (!currentCompany?.id) {
      console.log('No company ID available');
      return;
    }
    
    setLoading(true);
    try {
      console.log('Loading invoices for company:', currentCompany.id);
      const invoicesData = await invoicesService.getInvoices(currentCompany.id);
      console.log('Invoices loaded:', invoicesData);
      
      if (!invoicesData || invoicesData.length === 0) {
        console.log('No invoices found for company');
        setInvoices([]);
        return;
      }
      
      // Transform the data to include client information
      const invoicesWithClientInfo = await Promise.all(
        invoicesData.map(async (invoice) => {
          try {
            const client = invoice.clientId ? await clientsService.getClient(invoice.clientId) : null;
            return {
              ...invoice,
              clientName: client?.name || client?.companyName || 'Unknown Client',
              totalAmount: invoice.total || 0,
              vatAmount: invoice.vatAmount || 0,
              amount: invoice.subtotal || 0
            };
          } catch (clientError) {
            console.error('Error loading client info for invoice:', invoice.id, clientError);
            return {
              ...invoice,
              clientName: 'Unknown Client',
              totalAmount: invoice.total || 0,
              vatAmount: invoice.vatAmount || 0,
              amount: invoice.subtotal || 0
            };
          }
        })
      );
      
      // Sort invoices by creation date (latest first)
      const sortedInvoices = invoicesWithClientInfo.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA; // Latest first
      });
      
      setInvoices(sortedInvoices);
      console.log('Invoices with client info (sorted):', sortedInvoices);
    } catch (error) {
      console.error('Error loading invoices:', error);
      
      // Specific error handling for different Firebase error codes
      if (error.code === 'failed-precondition') {
        toast.error(t('invoices.error.index_required', 'Database index required. Please contact support.'));
      } else if (error.code === 'permission-denied') {
        toast.error(t('invoices.error.permission_denied', 'Permission denied. Please check your access rights.'));
      } else if (error.code === 'unavailable') {
        toast.error(t('invoices.error.service_unavailable', 'Service temporarily unavailable. Please try again.'));
      } else {
        toast.error(t('common.error_loading_data', 'Failed to load invoices. Please try again.'));
      }
      
      // Set empty array so UI doesn't break
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, t]);

  // Load invoices on component mount and company change
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  /**
   * Handle successful invoice creation
   * Refreshes the invoice list and closes the modal
   */
  const handleInvoiceCreated = () => {
    setShowCreateModal(false);
    loadInvoices(); // Refresh the invoices list
  };

  /**
   * Handle successful invoice update
   * Refreshes the invoice list and closes the edit modal
   */
  const handleInvoiceUpdated = () => {
    setShowEditModal(false);
    setEditingInvoice(null);
    loadInvoices(); // Refresh the invoices list
  };

  /**
   * Handle invoice deletion
   * Shows confirmation modal before deletion
   */
  const handleDeleteInvoice = (invoice) => {
    setInvoiceToDelete(invoice);
    setShowDeleteModal(true);
  };

  /**
   * Confirm and execute invoice deletion
   * Handles journal entry reversal and shipment relationship cleanup
   */
  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    
    try {
      console.log('🔄 Deleting invoice:', invoiceToDelete.invoiceNumber || invoiceToDelete.id);
      
      // Use the correct ID for deletion - prefer docId (Firestore document ID) over formatted invoice number
      const deleteId = invoiceToDelete.docId || invoiceToDelete.id;
      const invoiceNumber = invoiceToDelete.invoiceNumber || invoiceToDelete.id;
      console.log('🔄 Using ID for deletion:', deleteId);
      
      // Step 1: Find and reverse the automatic journal entry created for this invoice
      console.log('🔄 Step 1: Finding journal entries for invoice...');
      try {
        const journalEntries = await journalEntriesService.getJournalEntries(currentCompany.id);
        
        // Find journal entries related to this invoice
        const invoiceJournalEntries = journalEntries.filter(entry => 
          entry.sourceType === 'invoice' && 
          (entry.sourceId === deleteId || 
           entry.sourceId === invoiceNumber || 
           entry.reference === invoiceNumber ||
           entry.reference === deleteId ||
           (entry.description && entry.description.includes(invoiceNumber)))
        );
        
        console.log(`📋 Found ${invoiceJournalEntries.length} journal entries for invoice`);
        
        // Reverse each journal entry
        for (const journalEntry of invoiceJournalEntries) {
          if (journalEntry.status === 'posted') {
            console.log(`🔄 Reversing posted journal entry: ${journalEntry.entryNumber}`);
            await journalEntriesService.reverseJournalEntry(
              journalEntry.id, 
              currentCompany.id, // Using company ID as user ID for system operations
              `Automatic reversal due to invoice ${invoiceNumber} deletion`
            );
            console.log(`✅ Journal entry ${journalEntry.entryNumber} reversed successfully`);
          } else if (journalEntry.status === 'draft') {
            console.log(`🗑️ Deleting draft journal entry: ${journalEntry.entryNumber}`);
            await journalEntriesService.deleteJournalEntry(journalEntry.id);
            console.log(`✅ Draft journal entry ${journalEntry.entryNumber} deleted successfully`);
          }
        }
      } catch (journalError) {
        console.warn('⚠️ Error handling journal entries (non-critical):', journalError);
        // Don't fail the entire deletion if journal reversal fails
      }
      
      // Step 2: Clear shipment invoice relationships to make them available for invoicing again
      console.log('🔄 Step 2: Clearing shipment relationships...');
      if (invoiceToDelete.shipmentIds && invoiceToDelete.shipmentIds.length > 0) {
        try {
          await Promise.all(
            invoiceToDelete.shipmentIds.map(async (shipmentId) => {
              try {
                console.log(`🔄 Clearing invoice status for shipment: ${shipmentId}`);
                await shipmentsService.clearShipmentInvoiceStatus(shipmentId);
                console.log(`✅ Shipment ${shipmentId} is now available for invoicing`);
              } catch (shipmentError) {
                console.error(`❌ Error clearing invoice status for shipment ${shipmentId}:`, shipmentError);
              }
            })
          );
          console.log(`✅ All ${invoiceToDelete.shipmentIds.length} shipments are now available for invoicing`);
        } catch (shipmentsError) {
          console.warn('⚠️ Error clearing shipment relationships (non-critical):', shipmentsError);
          // Don't fail the entire deletion if shipment clearing fails
        }
      }
      
      // Step 3: Delete the invoice from the database
      console.log('🔄 Step 3: Deleting invoice from database...');
      await invoicesService.deleteInvoice(deleteId);
      
      console.log('✅ Invoice deletion completed successfully');
      toast.success(t('invoices.success.deleted', `Invoice ${invoiceNumber} deleted successfully. Journal entries reversed and shipments are now available for invoicing.`));
      
      // Refresh the invoices list
      loadInvoices();
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
      
    } catch (error) {
      console.error('❌ Error deleting invoice:', error);
      toast.error(t('invoices.error.deletion_failed', `Failed to delete invoice: ${error?.message || 'Unknown error'}`));
    }
  };

  /**
   * Handle invoice status change
   * Shows status change modal
   */
  const handleStatusChange = (invoice) => {
    setStatusChangeInvoice(invoice);
    setShowStatusModal(true);
  };

  /**
   * Update invoice status
   * Handles shipment relationship updates for cancelled invoices
   */
  const updateInvoiceStatus = async (newStatus) => {
    if (!statusChangeInvoice) return;
    
    try {
      // If status is being changed to cancelled, make shipments available again
      if (newStatus === 'cancelled' && statusChangeInvoice.shipmentIds && statusChangeInvoice.shipmentIds.length > 0) {
        await Promise.all(
          statusChangeInvoice.shipmentIds.map(async (shipmentId) => {
            try {
              await shipmentsService.clearShipmentInvoiceStatus(shipmentId);
            } catch (error) {
              console.error(`Error clearing invoice status for shipment ${shipmentId}:`, error);
            }
          })
        );
      }
      
      // Use the correct ID for updating - prefer docId (Firestore document ID) over formatted invoice number
      const updateId = statusChangeInvoice.docId || statusChangeInvoice.id;
      await invoicesService.updateInvoice(updateId, { status: newStatus });
      toast.success(t('invoices.success.status_updated', 'Invoice status updated successfully'));
      loadInvoices(); // Refresh the invoices list
      setShowStatusModal(false);
      setStatusChangeInvoice(null);
    } catch (error) {
      console.error('Error updating invoice status:', error);
      toast.error(t('invoices.error.status_update_failed', 'Failed to update invoice status'));
    }
  };

  /**
   * Handle payment recording
   * Opens payment recording modal
   */
  const handleRecordPayment = (invoice) => {
    setPaymentInvoice(invoice);
    setShowPaymentModal(true);
  };

  /**
   * Handle successful payment recording
   * Refreshes invoice list and closes payment modal
   */
  const handlePaymentRecorded = () => {
    setShowPaymentModal(false);
    setPaymentInvoice(null);
    loadInvoices(); // Refresh invoices
  };

  /**
   * Direct PDF download functionality
   * Generates and downloads invoice PDF using enhanced Firebase Function
   */
  const handleDirectDownload = async (invoice) => {
    try {
      console.log('🔄 Generating enhanced invoice PDF from InvoicesTab...');
      
      if (!invoice || !currentCompany?.id) {
        toast.error('Missing required data for PDF generation');
        return;
      }

      // Use the enhanced PDF service
      await invoicePDFService.generateInvoicePDF(
        invoice.id, 
        currentCompany.id, 
        'en' // You can make this dynamic based on user preference
      );
      
      console.log('✅ Enhanced PDF generated successfully from InvoicesTab');
      toast.success(t('invoices.success.pdf_downloaded', 'PDF downloaded successfully'));
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('invoices.error.pdf_generation_failed', 'Failed to generate PDF'));
    }
  };

  /**
   * Get status badge component with styling and optional click handler
   * Supports all invoice statuses with appropriate colors
   */
  const getStatusBadge = (status, clickable = false, onClick = null) => {
    const statusConfig = {
      draft: { class: 'badge text-white bg-slate-600 border-slate-600', text: t('invoices.status.draft', 'Draft') },
      sent: { class: 'badge text-white bg-blue-600 border-blue-600', text: t('invoices.status.sent', 'Sent') },
      pending: { class: 'badge text-white bg-amber-600 border-amber-600', text: t('invoices.status.pending', t('common.pending')) },
      partial: { class: 'badge text-white bg-orange-600 border-orange-600', text: t('invoices.status.partial', 'Partially Paid') },
      paid: { class: 'badge text-white bg-green-600 border-green-600', text: t('invoices.status.paid', t('common.paid')) },
      overdue: { class: 'badge text-white bg-red-600 border-red-600', text: t('invoices.status.overdue', t('common.overdue')) },
      cancelled: { class: 'badge text-white bg-purple-600 border-purple-600', text: t('invoices.status.cancelled', 'Cancelled') }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    const badgeClass = clickable ? `${config.class} cursor-pointer hover:opacity-75` : config.class;
    
    return (
      <span 
        className={badgeClass}
        onClick={clickable ? onClick : undefined}
        title={clickable ? t('invoices.click_to_change_status', 'Click to change status') : undefined}
      >
        {config.text}
      </span>
    );
  };

  // Filter invoices based on search term and status
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate summary statistics for dashboard cards
  const totalInvoices = invoices.length;
  const paidAmount = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.totalAmount, 0);
  const overdueAmount = invoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + inv.totalAmount, 0);
  const pendingAmount = invoices.filter(inv => inv.status === 'sent').reduce((sum, inv) => sum + inv.totalAmount, 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards - Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body text-center">
            <DocumentTextIcon className="w-8 h-8 mx-auto text-blue-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">{totalInvoices}</div>
            <div className="text-sm text-gray-500">{t('invoices.total_invoices', 'Total Invoices')}</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <CheckCircleIcon className="w-8 h-8 mx-auto text-green-600 mb-2" />
            <div className="text-2xl font-bold text-green-600">{formatCurrency(paidAmount)}</div>
            <div className="text-sm text-gray-500">{t('invoices.paid_amount', 'Paid Amount')}</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <ClockIcon className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
            <div className="text-2xl font-bold text-yellow-600">{formatCurrency(pendingAmount)}</div>
            <div className="text-sm text-gray-500">{t('invoices.pending_amount', 'Pending Amount')}</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <ExclamationTriangleIcon className="w-8 h-8 mx-auto text-red-600 mb-2" />
            <div className="text-2xl font-bold text-red-600">{formatCurrency(overdueAmount)}</div>
            <div className="text-sm text-gray-500">{t('invoices.overdue_amount', 'Overdue Amount')}</div>
          </div>
        </div>
      </div>

      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t('invoices.manage_invoices', 'Manage Invoices')}</h2>
          <p className="text-sm text-gray-500">{t('invoices.create_track_description', 'Create, send and track customer invoices')}</p>
        </div>
        
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {t('invoices.create_invoice', 'Create Invoice')}
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder={t('invoices.search_placeholder', 'Search invoices...')}
                  className="input input-bordered w-full pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex space-x-4">
              <select
                className="select select-bordered"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">{t('invoices.filter.all', 'All Statuses')}</option>
                <option value="draft">{t('invoices.filter.draft', 'Draft')}</option>
                <option value="sent">{t('invoices.filter.sent', 'Sent')}</option>
                <option value="pending">{t('invoices.filter.pending', t('common.pending'))}</option>
                <option value="partial">{t('invoices.filter.partial', 'Partially Paid')}</option>
                <option value="paid">{t('invoices.filter.paid', t('common.paid'))}</option>
                <option value="overdue">{t('invoices.filter.overdue', t('common.overdue'))}</option>
                <option value="cancelled">{t('invoices.filter.cancelled', 'Cancelled')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>{t('invoices.table.invoice_id', 'Invoice ID')}</th>
                    <th>{t('invoices.table.client', 'Client')}</th>
                    <th>{t('invoices.table.amount', t('common.amount'))}</th>
                    <th>{t('invoices.table.status', t('common.status'))}</th>
                    <th>{t('invoices.table.issue_date', 'Issue Date')}</th>
                    <th>{t('invoices.table.due_date', 'Due Date')}</th>
                    <th>{t('invoices.table.actions', t('common.actions'))}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8">
                        <div className="text-gray-500">
                          {t('invoices.no_invoices', 'No invoices found')}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="font-medium">{ensureEnglishNumerals(invoice.id)}</td>
                        <td>{invoice.clientName}</td>
                        <td className="font-semibold">{formatCurrency(invoice.totalAmount)}</td>
                        <td>{getStatusBadge(invoice.status, true, () => handleStatusChange(invoice))}</td>
                        <td>{invoice.issueDate ? formatDate(invoice.issueDate) : '-'}</td>
                        <td>{invoice.dueDate ? formatDate(invoice.dueDate) : '-'}</td>
                        <td>
                          <div className="flex space-x-2">
                            <button 
                              className="btn btn-sm btn-outline" 
                              onClick={() => setSelectedInvoice(invoice)}
                              title={t('invoices.view_invoice', 'View Invoice')}
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button 
                              className="btn btn-sm btn-outline btn-primary"
                              onClick={() => handleDirectDownload(invoice)}
                              title={t('invoices.download_pdf', 'Download PDF')}
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </button>
                            {/* Record Payment Button - only show for unpaid invoices */}
                            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                              <button 
                                className="btn btn-sm btn-outline btn-success"
                                onClick={() => handleRecordPayment(invoice)}
                                title={t('invoices.record_payment', 'Record Payment')}
                              >
                                <CreditCardIcon className="w-4 h-4" />
                              </button>
                            )}
                            <button 
                              className="btn btn-sm btn-outline btn-error"
                              onClick={() => handleDeleteInvoice(invoice)}
                              title={t('invoices.delete_invoice', 'Delete Invoice')}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      
      {/* Create Invoice Modal */}
      {showCreateModal && (
        <CreateInvoiceModal 
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleInvoiceCreated}
        />
      )}
      
      {/* Edit Invoice Modal */}
      {showEditModal && editingInvoice && (
        <EditInvoiceModal 
          invoice={editingInvoice}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleInvoiceUpdated}
        />
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{t('invoices.delete_confirmation', 'Delete Invoice')}</h3>
            <p className="text-gray-600 mb-4">
              {t('invoices.delete_warning', 'Are you sure you want to delete this invoice? This action cannot be undone.')}
            </p>
            <div className="flex justify-end space-x-2">
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-outline"
              >
                {t('common.cancel', t('common.cancel'))}
              </button>
              <button 
                onClick={confirmDeleteInvoice}
                className="btn btn-error"
              >
                {t('common.delete', t('common.delete'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{t('invoices.change_status', 'Change Invoice Status')}</h3>
            <div className="space-y-2">
              {['draft', 'sent', 'pending', 'partial', 'paid', 'overdue', 'cancelled'].map(status => (
                <button
                  key={status}
                  onClick={() => updateInvoiceStatus(status)}
                  className="w-full text-left px-3 py-2 rounded hover:bg-gray-100"
                >
                  {getStatusBadge(status)}
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button 
                onClick={() => setShowStatusModal(false)}
                className="btn btn-outline"
              >
                {t('common.cancel', t('common.cancel'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && paymentInvoice && (
        <RecordPaymentModal
          invoice={paymentInvoice}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentRecorded}
        />
      )}
    </div>
  );
};

export default InvoicesTab;